import React, { useState, useEffect } from 'react';
import { Phone, MapPin, Clock, Instagram, MessageCircle, X } from 'lucide-react';
import { storeInfo, brands } from '../data/mock';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const Home = () => {
  const [showStoryModal, setShowStoryModal] = useState(false);
  const [stores, setStores] = useState([]);
  const [collections, setCollections] = useState([]);
  const [storePhotos, setStorePhotos] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [storesRes, collectionsRes, photosRes, offersRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/stores`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/collections`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/store-photos`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/offers`),
      ]);

      if (storesRes.ok) setStores(await storesRes.json());
      if (collectionsRes.ok) setCollections(await collectionsRes.json());
      if (photosRes.ok) setStorePhotos(await photosRes.json());
      if (offersRes.ok) setOffers(await offersRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsAppClick = (whatsapp) => {
    const number = whatsapp || '918374695634'; // Default to main contact number
    const message = encodeURIComponent("Hi EPIC! I'm interested in your fashion collection.");
    
    // Use wa.me which tries to open app first, then falls back to web
    const url = `https://wa.me/${number}?text=${message}`;
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cream-white via-soft-gray to-cream-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-warm-sage mx-auto mb-4 flex items-center justify-center">
            <span className="text-cream-white font-serif text-3xl font-medium">E</span>
          </div>
          <p className="text-warm-gray">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-cream-white/95 backdrop-blur-md border-b border-soft-gray">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="relative">
                <img 
                  src="/epic-logo-header-official.png" 
                  alt="EPIC" 
                  className="h-16 w-auto"
                />
                <p className="text-[10px] text-warm-gray uppercase tracking-widest mt-1">Multi-Brand Outlet</p>
              </div>
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#collections" className="text-sm text-charcoal hover:text-warm-sage transition-colors uppercase tracking-wide">Collections</a>
              <a href="#stores" className="text-sm text-charcoal hover:text-warm-sage transition-colors uppercase tracking-wide">Our Stores</a>
              <a href="#brands" className="text-sm text-charcoal hover:text-warm-sage transition-colors uppercase tracking-wide">Brands</a>
              <a href="#locations" className="text-sm text-charcoal hover:text-warm-sage transition-colors uppercase tracking-wide">Visit Us</a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section - Brand Focus */}
      <section className="pt-32 md:pt-24 pb-16 bg-gradient-to-br from-cream-white via-soft-gray to-cream-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-5xl md:text-6xl font-serif text-charcoal leading-tight">
                Redefining<br />
                <span className="text-warm-sage">Men's Fashion</span>
              </h2>
              <p className="text-lg text-warm-gray leading-relaxed">
                18+ years of buying expertise. Pre-production ordering.<br />
                Four premium brands. Complete collections.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  className="bg-warm-sage hover:bg-warm-sage/90 text-cream-white px-8 py-6 text-sm uppercase tracking-wider"
                  onClick={() => document.getElementById('collections').scrollIntoView({ behavior: 'smooth' })}
                >
                  Explore Collections
                </Button>
                <Button 
                  variant="outline"
                  className="border-2 border-charcoal text-charcoal hover:bg-charcoal hover:text-cream-white px-8 py-6 text-sm uppercase tracking-wider"
                  onClick={() => setShowStoryModal(true)}
                >
                  Discover Our Story
                </Button>
                <Button 
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-sm uppercase tracking-wider"
                  onClick={() => handleWhatsAppClick('918374695634')}
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp Us
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/5] rounded-lg overflow-hidden shadow-2xl photo-vignette photo-glow">
                <img 
                  src="https://images.unsplash.com/photo-1625502709763-f5f3880c17ba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwzfHxtZW5zJTIwamFja2V0JTIwYmxhemVyJTIwZWxlZ2FudHxlbnwwfHx8fDE3Nzg0OTE4ODd8MA&ixlib=rb-4.1.0&q=85"
                  alt="Premium Men's Fashion"
                  className="w-full h-full object-cover store-photo-vibrant"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Makes Us Different Section */}
      <section className="py-16 bg-cream-white border-y border-warm-gray/20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-4xl font-serif text-charcoal mb-8">What Makes Us Different?</h3>
            <div className="space-y-4 text-lg text-charcoal leading-relaxed">
              <p className="font-medium">
                "That Jack & Jones style only in Mumbai? We have it."
              </p>
              <p className="font-medium">
                "That Rare Rabbit blazer sold out everywhere? On our racks."
              </p>
              <p className="font-medium">
                "That Benetton collection skipped for your city? You'll find it here."
              </p>
            </div>
            <p className="mt-8 text-warm-gray text-base max-w-2xl mx-auto">
              With 18+ years of retail expertise and pre-production ordering, we secure exclusive styles 
              that other stores miss. Three premium brands, complete collections, zero compromise.
            </p>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-12 bg-soft-gray">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl font-serif text-warm-sage mb-2">3</div>
              <p className="text-charcoal font-medium uppercase tracking-wider text-sm">Locations</p>
            </div>
            <div>
              <div className="text-4xl font-serif text-warm-sage mb-2">18+</div>
              <p className="text-charcoal font-medium uppercase tracking-wider text-sm">Years Retail Expertise</p>
            </div>
            <div>
              <div className="text-4xl font-serif text-warm-sage mb-2">4</div>
              <p className="text-charcoal font-medium uppercase tracking-wider text-sm">Premium Brands, Fully Stocked</p>
            </div>
          </div>
        </div>
      </section>

      {/* Brands Section with Logos */}
      <section id="brands" className="py-16 bg-soft-gray">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h3 className="text-4xl font-serif text-charcoal mb-4">Our Premium Brands</h3>
            <p className="text-warm-gray text-lg">Four brands. Complete collections. Always.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <Card className="bg-cream-white border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="h-32 flex items-center justify-center mb-6">
                  <img 
                    src="https://upload.wikimedia.org/wikipedia/commons/9/98/United_Colors_of_Benetton_-_logo_%28Italy%2C_2011%29.svg"
                    alt="United Colors of Benetton"
                    className="max-h-20 w-auto"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://1000logos.net/wp-content/uploads/2017/03/Benetton-Logo.png";
                    }}
                  />
                </div>
                <h4 className="text-xl font-medium text-charcoal mb-2">Benetton</h4>
                <p className="text-warm-gray text-sm uppercase tracking-wider">Italian Style & Colorful Casual Wear</p>
              </CardContent>
            </Card>
            
            <Card className="bg-cream-white border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="h-32 flex items-center justify-center mb-6">
                  <img 
                    src="https://thehouseofrare.com/cdn/shop/files/Frame_41820_250x.png"
                    alt="Rare Rabbit"
                    className="max-h-24 w-auto"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.parentElement.innerHTML = '<div class="text-5xl font-bold text-warm-sage">RARE RABBIT</div>';
                    }}
                  />
                </div>
                <h4 className="text-xl font-medium text-charcoal mb-2">Rare Rabbit</h4>
                <p className="text-warm-gray text-sm uppercase tracking-wider">Premium Contemporary Fashion</p>
              </CardContent>
            </Card>
            
            <Card className="bg-cream-white border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="h-32 flex items-center justify-center mb-6">
                  <img 
                    src="https://upload.wikimedia.org/wikipedia/commons/7/78/Jack_%26_Jones_logo.svg"
                    alt="Jack & Jones"
                    className="max-h-16 w-auto"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://1000logos.net/wp-content/uploads/2020/09/Jack-Jones-logo.png";
                    }}
                  />
                </div>
                <h4 className="text-xl font-medium text-charcoal mb-2">Jack & Jones</h4>
                <p className="text-warm-gray text-sm uppercase tracking-wider">Scandinavian Denim & Streetwear</p>
              </CardContent>
            </Card>

            <Card className="bg-cream-white border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="h-32 flex items-center justify-center mb-6">
                  <img 
                    src="https://static.prod-images.emergentagent.com/jobs/11c2ff2c-431c-49b1-aa6f-254f40e3d877/images/f286a2bf60ddc6e35e49be7fa4ea6d65600fc41bb17d0644fcc1ed3f462e8b23.jpeg"
                    alt="Pepe Jeans London"
                    className="max-h-20 w-auto"
                  />
                </div>
                <h4 className="text-xl font-medium text-charcoal mb-2">Pepe Jeans London</h4>
                <p className="text-warm-gray text-sm uppercase tracking-wider">Iconic London Denim & Streetwear</p>
              </CardContent>
            </Card>
          </div>
          
          <div className="mt-12 text-center max-w-3xl mx-auto">
            <p className="text-charcoal text-lg leading-relaxed">
              <span className="font-medium text-warm-sage">Quality over quantity.</span> We chose the top 4 premium brands 
              and gave them the space they deserve. Complete collections, not fragments.
            </p>
          </div>
        </div>
      </section>

      {/* Collections Section */}
      <section id="collections" className="py-16 bg-soft-gray">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h3 className="text-4xl font-serif text-charcoal mb-4">Curated Excellence</h3>
            <p className="text-warm-gray text-lg">Every piece, every brand, thoughtfully selected</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {collections.map((collection) => (
              <Card key={collection.id} className="bg-cream-white border-none shadow-lg overflow-hidden group hover:shadow-2xl transition-all photo-glow">
                <div className="aspect-[3/4] overflow-hidden relative photo-vignette">
                  <img 
                    src={collection.image}
                    alt={collection.title}
                    className="w-full h-full object-cover pro-photo-collection"
                  />
                </div>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <Badge className="bg-warm-sage/10 text-warm-sage border-none uppercase text-xs tracking-wider">
                      {collection.category}
                    </Badge>
                    <span className="text-xs text-warm-gray uppercase tracking-wider">{collection.brand}</span>
                  </div>
                  <h4 className="text-xl font-serif text-charcoal mb-2">{collection.title}</h4>
                  <p className="text-warm-gray text-sm">{collection.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Our Stores Section - Visual Gallery */}
      <section id="stores" className="py-16 bg-cream-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h3 className="text-4xl font-serif text-charcoal mb-4">Our Stores</h3>
            <p className="text-warm-gray text-lg">Experience EPIC. Three locations.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {storePhotos.map((photo) => (
              <div key={photo.id} className="aspect-[4/3] rounded-lg overflow-hidden shadow-2xl photo-vignette photo-glow">
                <img 
                  src={photo.image}
                  alt={photo.alt}
                  className="w-full h-full object-cover store-photo-vibrant"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Offers Section */}
      <section id="offers" className="py-16 bg-soft-gray">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h3 className="text-4xl font-serif text-charcoal mb-4">Exclusive Offers</h3>
            <p className="text-warm-gray text-lg">Discover seasonal promotions and special deals</p>
          </div>
          <div className="max-w-4xl mx-auto space-y-6">
            {offers.length > 0 ? (
              offers.map((offer) => (
                <Card key={offer.id} className="bg-cream-white border-2 border-warm-sage shadow-xl hover:shadow-2xl transition-all">
                  <CardContent className="p-12 text-center">
                    <Badge className="bg-warm-sage text-cream-white border-none uppercase text-xs tracking-wider mb-6 inline-block">
                      {offer.badge}
                    </Badge>
                    <h4 className="text-3xl md:text-4xl font-serif text-charcoal mb-6 leading-tight">
                      {offer.title}
                    </h4>
                    <p className="text-warm-gray text-lg mb-8 leading-relaxed max-w-2xl mx-auto">
                      {offer.description}
                    </p>
                    {offer.valid_until && (
                      <p className="text-warm-sage text-sm mb-6">Valid until: {offer.valid_until}</p>
                    )}
                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                      <Button 
                        className="bg-warm-sage hover:bg-warm-sage/90 text-cream-white px-8 py-6 text-sm uppercase tracking-wider"
                        onClick={() => document.getElementById('locations').scrollIntoView({ behavior: 'smooth' })}
                      >
                        <MapPin className="w-5 h-5 mr-2" />
                        {offer.cta}
                      </Button>
                      <Button 
                        className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-sm uppercase tracking-wider"
                        onClick={() => handleWhatsAppClick('918374695634')}
                      >
                        <MessageCircle className="w-5 h-5 mr-2" />
                        Ask About Offers
                      </Button>
                    </div>
                    <div className="mt-8 pt-8 border-t border-warm-gray/20">
                      <p className="text-warm-gray text-sm uppercase tracking-wider">
                        <Clock className="w-4 h-4 inline mr-2" />
                        {stores[0]?.timings || 'Open 365 Days | 10:30 AM - 9:30 PM'}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card className="bg-cream-white border-2 border-warm-sage shadow-xl">
                <CardContent className="p-12 text-center">
                  <p className="text-warm-gray text-lg">Check back soon for exciting offers!</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Locations Section */}
      <section id="locations" className="py-16 bg-cream-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h3 className="text-4xl font-serif text-charcoal mb-4">Visit Us</h3>
            <p className="text-warm-gray text-lg">Three locations, one standard of excellence</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {stores.map((location) => (
              <Card key={location.id} className="bg-soft-gray border-none shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <Badge className="bg-warm-sage/10 text-warm-sage border-none uppercase text-xs tracking-wider mb-4">
                    {location.type}
                  </Badge>
                  <h4 className="text-2xl font-serif text-charcoal mb-3">{location.name}</h4>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-start space-x-3">
                      <MapPin className="w-4 h-4 text-warm-sage mt-1 flex-shrink-0" />
                      <p className="text-warm-gray text-sm">{location.address}</p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Phone className="w-4 h-4 text-warm-sage mt-1 flex-shrink-0" />
                      <a href={`tel:${location.phone}`} className="text-warm-gray text-sm hover:text-warm-sage transition-colors">
                        {location.phone}
                      </a>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline"
                      className="flex-1 border-warm-sage text-warm-sage hover:bg-warm-sage hover:text-cream-white py-4 text-xs uppercase tracking-wider"
                      onClick={() => window.open(location.maps_url, '_blank')}
                    >
                      <MapPin className="w-4 h-4 mr-2" />
                      Directions
                    </Button>
                    <Button 
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white py-4 text-xs uppercase tracking-wider"
                      onClick={() => handleWhatsAppClick(location.whatsapp)}
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      WhatsApp
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-charcoal text-cream-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-10 h-10 rounded-full bg-warm-sage flex items-center justify-center">
                  <span className="text-cream-white font-serif text-xl font-medium">E</span>
                </div>
                <div>
                  <h4 className="text-xl font-serif">EPIC</h4>
                  <p className="text-xs text-warm-gray uppercase tracking-wider">Since 2022</p>
                </div>
              </div>
              <p className="text-warm-gray text-sm leading-relaxed">
                Redefining men's fashion with 18+ years of retail expertise. Three premium brands, complete collections, zero compromise.
              </p>
            </div>
            
            <div>
              <h5 className="text-sm uppercase tracking-wider mb-4 text-warm-sage">Quick Links</h5>
              <ul className="space-y-2">
                <li><a href="#collections" className="text-warm-gray hover:text-warm-sage transition-colors text-sm">Collections</a></li>
                <li><a href="#stores" className="text-warm-gray hover:text-warm-sage transition-colors text-sm">Our Stores</a></li>
                <li><a href="#brands" className="text-warm-gray hover:text-warm-sage transition-colors text-sm">Brands</a></li>
                <li><a href="#locations" className="text-warm-gray hover:text-warm-sage transition-colors text-sm">Locations</a></li>
              </ul>
            </div>

            <div>
              <h5 className="text-sm uppercase tracking-wider mb-4 text-warm-sage">Get in Touch</h5>
              <ul className="space-y-2">
                <li>
                  <a href="tel:+918374695634" className="text-warm-gray hover:text-warm-sage transition-colors text-sm flex items-center">
                    <Phone className="w-3 h-3 mr-2" />
                    +91 83746 95634
                  </a>
                </li>
                <li>
                  <button 
                    onClick={() => handleWhatsAppClick('918374695634')}
                    className="text-warm-gray hover:text-warm-sage transition-colors text-sm flex items-center cursor-pointer">
                    <MessageCircle className="w-3 h-3 mr-2" />
                    WhatsApp
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleWhatsAppClick('918374695634')}
                    className="text-warm-gray hover:text-warm-sage transition-colors text-sm text-left cursor-pointer"
                  >
                    Careers
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h5 className="text-sm uppercase tracking-wider mb-4 text-warm-sage">Connect With Us</h5>
              <p className="text-warm-gray text-sm mb-4">{storeInfo.timings}</p>
              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  className="border-warm-sage text-warm-sage hover:bg-warm-sage hover:text-cream-white text-xs uppercase tracking-wider flex-1"
                  onClick={() => window.open(storeInfo.instagram, '_blank')}
                >
                  <Instagram className="w-4 h-4 mr-2" />
                  Instagram
                </Button>
                <Button 
                  variant="outline"
                  className="border-green-600 text-green-600 hover:bg-green-600 hover:text-white text-xs uppercase tracking-wider flex-1"
                  onClick={() => handleWhatsAppClick('918374695634')}
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  WhatsApp
                </Button>
              </div>
            </div>
          </div>

          <div className="border-t border-warm-gray/20 pt-8 text-center">
            <p className="text-warm-gray text-sm mb-2">
              EPIC - Part of K J Fashions Group
            </p>
            <p className="text-warm-gray text-sm">
              © 2024 EPIC Multi-Brand Outlet. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <button
        onClick={() => handleWhatsAppClick('918374695634')}
        className="fixed bottom-6 right-6 bg-green-600 hover:bg-green-700 text-white rounded-full p-4 shadow-2xl z-50 hover:scale-110 transition-transform"
        aria-label="Chat on WhatsApp"
      >
        <MessageCircle className="w-6 h-6" />
      </button>

      {/* Story Modal */}
      {showStoryModal && (
        <div className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4 backdrop-blur-sm" onClick={() => setShowStoryModal(false)}>
          <div className="bg-cream-white rounded-lg max-w-4xl max-h-[90vh] overflow-y-auto relative" onClick={(e) => e.stopPropagation()}>
            <button 
              onClick={() => setShowStoryModal(false)}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-charcoal/10 hover:bg-charcoal/20 flex items-center justify-center transition-colors z-10"
            >
              <X className="w-5 h-5 text-charcoal" />
            </button>
            
            <div className="p-8 md:p-12">
              <h2 className="text-4xl md:text-5xl font-serif text-charcoal mb-6">The EPIC Story</h2>
              <p className="text-sm uppercase tracking-wider text-warm-sage mb-8">From Experience to Excellence - Since 2022</p>
              
              <div className="space-y-8 text-warm-gray leading-relaxed">
                <div className="bg-warm-sage/10 p-6 rounded-lg border-l-4 border-warm-sage">
                  <h3 className="text-xl font-serif text-charcoal mb-3">Part of K J Fashions Group</h3>
                  <p className="text-charcoal">
                    EPIC is proudly part of the <span className="font-semibold">K J Fashions Group</span>, 
                    industry leaders with over 18 years of excellence in premium fashion retail. K J Fashions Group 
                    has successfully established and managed multiple exclusive brand store outlets across India, 
                    earning a reputation for quality, expertise, and customer satisfaction.
                  </p>
                  <p className="mt-3 text-charcoal">
                    Our success is driven by a dedicated team of <span className="font-medium">professional buyers, 
                    merchandisers, and fashion specialists</span> who bring deep industry knowledge and strategic 
                    partnerships with leading international brands.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-serif text-charcoal mb-4">18 Years of Expertise. One Bold Vision.</h3>
                  <p>
                    After nearly two decades in fashion retail and buying under the K J Fashions Group, we saw the same 
                    pattern everywhere: multi-brand outlets cramming 10+ brands into fragmented spaces, and exclusive 
                    brand stores struggling with limited inventory.
                  </p>
                  <p className="mt-4">
                    Customers were left choosing between incomplete collections at MBOs or sold-out styles at EBOs. 
                    We knew there was a better way.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-serif text-charcoal mb-4">2022: EPIC Is Born</h3>
                  <p>
                    In 2022, we launched with a radical approach:
                  </p>
                  <ul className="mt-4 space-y-2 list-disc list-inside">
                    <li>Four premium brands (Benetton, Rare Rabbit, Jack & Jones, Pepe Jeans London)</li>
                    <li>Complete collections, not fragments</li>
                    <li>Pre-production buying for exclusive access</li>
                    <li>Proper space for each brand to shine</li>
                  </ul>
                  <p className="mt-4">
                    The market was skeptical. Customers weren't.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-serif text-charcoal mb-4">Immediate Success</h3>
                  <p>
                    While neighboring exclusive brand stores grappled with stock-outs and limited selections, EPIC thrived from day one.
                  </p>
                  <p className="mt-4">
                    Why? Because 18 years of buying intelligence meant we knew exactly what customers wanted, secured styles before they were made, stocked the sizes that actually sell, and curated collections that never felt incomplete.
                  </p>
                  <p className="mt-4 font-medium text-charcoal">
                    Word spread fast. The modern man found his destination.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-serif text-charcoal mb-4">Confident Expansion</h3>
                  <p>
                    Most new retail concepts take years to prove themselves. EPIC's success was immediate and undeniable.
                  </p>
                  <p className="mt-4">
                    Backed by proven buying expertise and validated by customer response, we confidently expanded:
                  </p>
                  <ul className="mt-4 space-y-2">
                    <li className="flex items-start space-x-2">
                      <span className="text-warm-sage">→</span>
                      <span><strong>L&T Hyderabad:</strong> Bringing the EPIC experience to the metro</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-warm-sage">→</span>
                      <span><strong>Rajahmundry:</strong> India's exclusive kids fashion destination</span>
                    </li>
                  </ul>
                  <p className="mt-4">
                    Each store built on the same foundation that made our first location an instant success story.
                  </p>
                </div>

                <div className="border-t border-warm-gray/20 pt-8">
                  <h3 className="text-2xl font-serif text-charcoal mb-4">The EPIC Advantage</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium text-charcoal mb-2">Pre-Production Ordering</h4>
                      <p className="text-sm">
                        We secure fashion before it's made. That Mumbai-exclusive piece? We ordered it months ago.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-medium text-charcoal mb-2">Complete Collections</h4>
                      <p className="text-sm">
                        Three brands with full representation. Not fragments, but the complete story.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-medium text-charcoal mb-2">18+ Years Intelligence</h4>
                      <p className="text-sm">
                        Data-driven buying means the styles you want are always in stock, in your size.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-medium text-charcoal mb-2">Quality Over Quantity</h4>
                      <p className="text-sm">
                        We chose the top 4 premium brands and gave them the space they deserve. Complete collections, not fragments.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-warm-sage/10 p-6 rounded-lg">
                  <p className="text-charcoal font-medium text-center text-lg">
                    This is why EPIC succeeded where others struggled.<br />
                    This is why we're growing while others stagnate.<br />
                    <span className="text-warm-sage">This is retail done right.</span>
                  </p>
                </div>
              </div>

              <div className="mt-8 flex flex-wrap gap-4 justify-center">
                <Button 
                  className="bg-warm-sage hover:bg-warm-sage/90 text-cream-white px-8 py-4 text-sm uppercase tracking-wider"
                  onClick={() => {
                    setShowStoryModal(false);
                    document.getElementById('locations').scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  Visit Our Stores
                </Button>
                <Button 
                  variant="outline"
                  className="border-2 border-charcoal text-charcoal hover:bg-charcoal hover:text-cream-white px-8 py-4 text-sm uppercase tracking-wider"
                  onClick={() => {
                    setShowStoryModal(false);
                    document.getElementById('collections').scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  Explore Collections
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
